/**
 * usePixelPerfectZoom Hook
 *
 * Manages pixel-perfect zoom with trackpad gesture support using a translation layer
 * between our domain model (100% = native size) and CSS transform scaling.
 *
 * Features:
 * - Pixel-perfect 100% zoom (1 source pixel = 1 physical pixel on DPR displays)
 * - Trackpad pinch-to-zoom gestures
 * - Smart variant selection based on exact target dimensions
 * - Translation layer hides CSS transform complexity
 *
 * @module usePixelPerfectZoom
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import type { Photo } from '@/types/photo';
import type { ZoomLevel } from '@/hooks/useZoomPan';
import type { ZoomPanContextValue } from '@/contexts/ZoomPanContext';
import { getVariantUrl, selectBestVariant } from '@/components/photos/AdaptivePhoto';
import {
  translateOurZoomToTransformScale,
  translateTransformScaleToOurZoom,
  calculateTargetDimensions,
} from '@/utils/zoomTranslation';
import logger from '@/utils/logger';

interface UsePixelPerfectZoomOptions {
  photo: Photo;
  sharedState?: ZoomPanContextValue;
  onImageSourceChange?: (url: string) => void;
}

interface UsePixelPerfectZoomReturn {
  // Our domain model (external API)
  ourZoomLevel: ZoomLevel;
  setOurZoomLevel: (level: ZoomLevel) => void;

  // Image state
  imageSource: string;
  isLoading: boolean;

  // Transform state (for TransformWrapper)
  transformScale: number;
  transformRef: React.RefObject<ReactZoomPanPinchRef>;

  // Refs
  imageRef: React.RefObject<HTMLImageElement>;
  containerRef: React.RefObject<HTMLDivElement>;

  // Container size for variant selection
  containerSize: { width: number; height: number };

  // Callbacks
  handleImageLoad: (img: HTMLImageElement) => void;
  handleTransformed: (ref: ReactZoomPanPinchRef) => void;
  getCursorStyle: () => string;
}

// Delay before loading optimal image after zoom stops (ms)
// Animation takes 200ms, then wait 250ms after completion = 450ms total
const IMAGE_LOAD_DEBOUNCE_MS = 450;

export function usePixelPerfectZoom({
  photo,
  sharedState,
  onImageSourceChange,
}: UsePixelPerfectZoomOptions): UsePixelPerfectZoomReturn {
  // Use shared zoom level if available, otherwise local state
  const [localZoomLevel, setLocalZoomLevel] = useState<ZoomLevel>('fit');
  const ourZoomLevel = sharedState?.zoomLevel ?? localZoomLevel;

  // Debounced zoom level for image variant selection
  // This prevents image loading during zoom gestures
  const [stableZoomLevel, setStableZoomLevel] = useState<ZoomLevel>('fit');

  const [initialScaleFactor, setInitialScaleFactor] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track the last known image source to detect changes
  const imageSourceRef = useRef<string>('');

  // Track when we're programmatically applying zoom to prevent reentrancy
  const isApplyingZoomRef = useRef(false);

  const dpr = window.devicePixelRatio || 1;

  /**
   * Debounce zoom level changes for image variant selection
   * This prevents loading new images during smooth zoom gestures
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (stableZoomLevel !== ourZoomLevel) {
        logger.debug('PixelPerfectZoom', 'Updating stable zoom level:', {
          from: stableZoomLevel,
          to: ourZoomLevel,
        });
        setStableZoomLevel(ourZoomLevel);
      }
    }, IMAGE_LOAD_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [ourZoomLevel, stableZoomLevel]);

  /**
   * Calculate exact target dimensions for our zoom level
   * This is what we need for pixel-perfect rendering
   */
  const targetDimensions = useMemo(() => {
    if (ourZoomLevel === 'fit') {
      return null;
    }

    if (!photo.width || !photo.height) {
      return null;
    }

    return calculateTargetDimensions(
      photo.width,
      photo.height,
      ourZoomLevel,
      dpr
    );
  }, [ourZoomLevel, photo.width, photo.height, dpr]);

  /**
   * Calculate target dimensions for stable (debounced) zoom level
   * Used for variant selection to avoid loading during zoom gestures
   */
  const stableTargetDimensions = useMemo(() => {
    if (stableZoomLevel === 'fit') {
      return null;
    }

    if (!photo.width || !photo.height) {
      return null;
    }

    return calculateTargetDimensions(
      photo.width,
      photo.height,
      stableZoomLevel,
      dpr
    );
  }, [stableZoomLevel, photo.width, photo.height, dpr]);

  /**
   * Select best image variant for target dimensions
   * Uses stableZoomLevel to prevent loading during zoom gestures
   */
  const imageVariant = useMemo(() => {
    if (!photo.variants || photo.variants.length === 0) {
      return null;
    }

    // At fit mode, use medium to save bandwidth
    if (stableZoomLevel === 'fit') {
      return selectBestVariant(photo.variants, containerSize.width, containerSize.height);
    }

    // At zoom levels, use exact target dimensions
    if (!stableTargetDimensions) {
      return selectBestVariant(photo.variants, containerSize.width, containerSize.height);
    }

    return selectBestVariant(
      photo.variants,
      stableTargetDimensions.width,
      stableTargetDimensions.height
    );
  }, [stableZoomLevel, stableTargetDimensions, photo.variants, containerSize.width, containerSize.height]);

  /**
   * Get image source URL for selected variant
   * Feature 081: Always use storageUrl for version-correct image display
   */
  const imageSource = useMemo(() => {
    if (!imageVariant) {
      // Fallback to first variant
      return photo.variants?.[0]?.storageUrl || '';
    }

    // Feature 081: Use preferStorageUrl=true to ensure version-specific URLs
    // publicUrl redirects always serve current version; storageUrl is direct
    return getVariantUrl(photo, imageVariant, true) || '';
  }, [photo, imageVariant]);

  /**
   * Translate our zoom to TransformWrapper scale
   */
  const transformScale = useMemo(() => {
    const scale = translateOurZoomToTransformScale(
      ourZoomLevel,
      dpr,
      initialScaleFactor
    );

    logger.debug('PixelPerfectZoom', 'Calculated transform scale:', {
      ourZoomLevel,
      dpr,
      initialScaleFactor,
      transformScale: scale,
    });

    return scale;
  }, [ourZoomLevel, dpr, initialScaleFactor]);

  /**
   * Detect image source changes and show loading state
   */
  useEffect(() => {
    if (imageSource !== imageSourceRef.current) {
      logger.debug('PixelPerfectZoom', 'Image source changing:', {
        from: imageSourceRef.current,
        to: imageSource,
      });

      imageSourceRef.current = imageSource;
      setIsLoading(true);

      onImageSourceChange?.(imageSource);

      if (sharedState) {
        sharedState.onLoadingChange(true);
      }
    }
  }, [imageSource, onImageSourceChange, sharedState]);

  /**
   * When image loads, calculate initial scale factor and clear loading state
   *
   * IMPORTANT: We use original photo dimensions (not variant dimensions) to
   * calculate the scale factor. This keeps it stable when switching variants,
   * preventing a two-stage zoom animation.
   */
  const handleImageLoad = useCallback((img: HTMLImageElement) => {
    // Guard against zero dimensions (can happen during unmount or hidden containers)
    if (img.clientWidth === 0 || img.clientHeight === 0) {
      logger.warn('PixelPerfectZoom', 'Image has zero dimensions, skipping scale calculation');
      setIsLoading(false);
      if (sharedState) {
        sharedState.onLoadingChange(false);
      }
      return;
    }

    // Use original photo dimensions to keep scale factor stable across variants
    const originalWidth = photo.width || img.naturalWidth;
    const originalHeight = photo.height || img.naturalHeight;

    // Guard against zero original dimensions
    const safeOriginalWidth = Math.max(originalWidth, 1);
    const safeOriginalHeight = Math.max(originalHeight, 1);

    const scaleX = img.clientWidth / safeOriginalWidth;
    const scaleY = img.clientHeight / safeOriginalHeight;
    const factor = Math.min(scaleX, scaleY);

    setInitialScaleFactor(factor);

    logger.info('PixelPerfectZoom', 'Image loaded, calculated scale factor:', {
      originalSize: `${originalWidth}×${originalHeight}`,
      variantSize: `${img.naturalWidth}×${img.naturalHeight}`,
      displaySize: `${img.clientWidth}×${img.clientHeight}`,
      initialScaleFactor: factor,
      explanation: `object-contain scaled image to ${(factor * 100).toFixed(1)}% of original size`,
    });

    // Clear loading state
    setIsLoading(false);

    if (sharedState) {
      sharedState.onLoadingChange(false);
    }
  }, [photo.width, photo.height, sharedState]);

  /**
   * Apply our zoom level to TransformWrapper when zoom changes
   */
  useEffect(() => {
    if (!transformRef.current) {
      logger.debug('PixelPerfectZoom', 'transformRef not ready, skipping zoom application');
      return;
    }

    const currentScale = transformRef.current.instance.transformState.scale;

    // If we're already at the target scale, skip (user just scrolled wheel)
    // This prevents double-updates when zoom changes from user gesture
    // Use 0.05 threshold to account for rounding errors in zoom percentage translation
    if (ourZoomLevel !== 'fit' && Math.abs(currentScale - transformScale) < 0.05) {
      logger.debug('PixelPerfectZoom', 'Already at target scale, skipping centerView:', {
        currentScale,
        transformScale,
        diff: Math.abs(currentScale - transformScale),
      });
      return;
    }

    logger.info('PixelPerfectZoom', 'Applying zoom level:', {
      ourZoomLevel,
      transformScale,
      targetDimensions,
      currentScale,
    });

    // Set flag to prevent handleTransformed from creating feedback loop
    isApplyingZoomRef.current = true;

    if (ourZoomLevel === 'fit') {
      transformRef.current.resetTransform(200); // 200ms animation
    } else {
      // Center view at calculated scale
      transformRef.current.centerView(transformScale, 200);
    }

    // Clear flag after animation completes (200ms + 100ms buffer)
    const timeoutId = setTimeout(() => {
      isApplyingZoomRef.current = false;
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [ourZoomLevel, transformScale, targetDimensions]);

  /**
   * When user pinch-zooms with trackpad, translate back to our model
   * and update zoom level to reflect current state
   */
  const handleTransformed = useCallback((ref: ReactZoomPanPinchRef) => {
    // Skip if we're programmatically applying a zoom change (prevents reentrancy)
    if (isApplyingZoomRef.current) {
      logger.debug('PixelPerfectZoom', 'Skipping update - programmatic zoom in progress');
      return;
    }

    const { scale, positionX, positionY } = ref.instance.transformState;

    logger.info('PixelPerfectZoom', 'Transform state changed:', {
      cssScale: scale,
      position: { x: positionX, y: positionY },
      currentZoomLevel: ourZoomLevel,
      initialScaleFactor,
      dpr,
    });

    // If we're in 'fit' mode and scale is at rest (close to 1.0), don't update
    // This prevents resetTransform from fighting with fit mode
    if (ourZoomLevel === 'fit' && Math.abs(scale - 1.0) < 0.05) {
      logger.debug('PixelPerfectZoom', 'In fit mode at rest, skipping update');
      return;
    }

    // Translate CSS scale to our zoom percentage
    const ourZoom = translateTransformScaleToOurZoom(
      scale,
      dpr,
      initialScaleFactor
    );

    logger.debug('PixelPerfectZoom', 'Translated to our zoom:', {
      ourZoomPercent: ourZoom.toFixed(1),
    });

    // Update zoom level to reflect user's trackpad gesture
    // Round to nearest integer for cleaner display
    const roundedZoom = Math.round(ourZoom);

    // Calculate what "fit" actually means as a percentage for smooth comparisons
    // This prevents jumps when scrolling from fit mode
    const fitZoomPercent = initialScaleFactor * dpr * 100;
    const currentNumeric = typeof ourZoomLevel === 'number' ? ourZoomLevel : fitZoomPercent;

    logger.info('PixelPerfectZoom', 'Zoom update decision:', {
      roundedZoom,
      currentNumeric,
      fitZoomPercent,
      difference: Math.abs(roundedZoom - currentNumeric),
      willUpdate: Math.abs(roundedZoom - currentNumeric) > 1,
    });

    // Only update if it's a meaningful change (> 1% difference)
    if (Math.abs(roundedZoom - currentNumeric) > 1) {
      logger.info('PixelPerfectZoom', 'Updating zoom level:', {
        from: ourZoomLevel,
        to: roundedZoom,
      });

      if (sharedState) {
        sharedState.onZoomChange(roundedZoom as ZoomLevel);
      } else {
        setLocalZoomLevel(roundedZoom as ZoomLevel);
      }
    }
  }, [dpr, initialScaleFactor, ourZoomLevel, sharedState]);

  /**
   * Observe container size changes for variant selection
   */
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  /**
   * Get cursor style based on zoom level
   * Shows zoom-in at fit level, grab (for panning) when zoomed
   */
  const getCursorStyle = useCallback((): string => {
    if (ourZoomLevel === 'fit') {
      return 'zoom-in';
    }
    return 'grab';
  }, [ourZoomLevel]);

  /**
   * Public API for setting zoom level
   */
  const setOurZoomLevel = useCallback((level: ZoomLevel) => {
    logger.info('PixelPerfectZoom', 'Zoom level changed:', { level });

    if (sharedState) {
      // Update shared state (for multi-photo synchronization)
      sharedState.onZoomChange(level);
    } else {
      // Update local state
      setLocalZoomLevel(level);
    }
  }, [sharedState]);

  return {
    ourZoomLevel,
    setOurZoomLevel,
    imageSource,
    isLoading,
    transformScale,
    transformRef,
    imageRef,
    containerRef,
    containerSize,
    handleImageLoad,
    handleTransformed,
    getCursorStyle,
  };
}
